<!--
  PlacementZoneClasses -- SuperAdmin ops-review surface for the
  placement_zone_classes taxonomy.

  Decoration Model Refactor -- Phase P2 (PR-C).
  Card: Boulders #163 (https://trello.com/c/S5kN5poU)

  Why this page exists
  ====================
  PR-B's seed migration ran the PR-A clustering algorithm against
  the current placement_zones and INSERTed one row per proposed
  class. This page lets TF ops inspect those rows, override per-zone
  assignments, and re-run clustering when a new vendor catalog
  arrives.

  IA
  ==
  Two-pane layout.

  LEFT: list of placement_zone_classes rows -- name, envelope dims,
        aspect-ratio band, member count. Clicking a row selects it.

  RIGHT: when a class is selected, the list of placement_zones with
         that class_id. Each row shows slug, display name, the zone's
         own max dims (so the reviewer can spot envelope drift), and
         a dropdown to re-assign the zone to a different class (or
         to NULL = "unclassified").

  TOP: "Re-cluster all zones" action that POSTs to /recluster and
       shows the proposed diff in a modal-ish strip. Apply actions
       are per-zone (the diff is preview-only; nothing persists
       until the reviewer PATCHes).

  Endpoints
  =========
  * GET   /api/admin/placement-zone-classes
  * GET   /api/admin/placement-zone-classes/<id>/zones
  * PATCH /api/admin/placement-zones/<id>/class
  * POST  /api/admin/placement-zone-classes/recluster

  Mounted at /admin/placement-zone-classes via
  router/groups/superadmin.js. Gated on superadmin.settings.

  data-testid prefix: zone-classes-*
-->
<template>
    <section
        class="zone-classes"
        data-testid="zone-classes-root"
    >
        <header class="zone-classes__header">
            <h2 class="zone-classes__title">
                Placement zone classes
            </h2>
            <button
                type="button"
                class="tf-btn tf-btn--ghost"
                :disabled="reclustering"
                data-testid="zone-classes-recluster"
                @click="onRecluster"
            >
                {{ reclustering ? "Reclustering..." : "Re-cluster all zones" }}
            </button>
        </header>

        <p class="zone-classes__lede">
            Each class is a max-dimension envelope grouping. A
            decoration that fits the envelope can be reused across
            any zone in the class without re-digitization. Add a
            class via a new vendor catalog import, or override
            per-zone assignments below.
        </p>

        <div
            v-if="loading"
            data-testid="zone-classes-loading"
        >
            Loading...
        </div>

        <div
            v-else
            class="zone-classes__panes"
        >
            <aside
                class="zone-classes__list"
                data-testid="zone-classes-list"
            >
                <div
                    v-if="!classes.length"
                    class="zone-classes__empty"
                    data-testid="zone-classes-empty-list"
                >
                    No zone classes yet. Run "Re-cluster all zones"
                    to seed from the current placement_zones.
                </div>
                <button
                    v-for="cls in classes"
                    :key="cls.id"
                    type="button"
                    class="zone-classes__row"
                    :class="{
                        'zone-classes__row--active':
                            selectedClassId === cls.id,
                    }"
                    :data-testid="`zone-classes-row-${cls.id}`"
                    @click="onSelectClass(cls.id)"
                >
                    <span class="zone-classes__row-name">
                        {{ cls.name }}
                    </span>
                    <span class="zone-classes__row-dims">
                        {{ cls.max_width_in }}" x
                        {{ cls.max_height_in }}"
                    </span>
                    <span class="zone-classes__row-count">
                        {{ cls.member_count }}
                        member{{ cls.member_count === 1 ? "" : "s" }}
                    </span>
                </button>
            </aside>

            <section
                class="zone-classes__detail"
                data-testid="zone-classes-detail"
            >
                <div
                    v-if="!selectedClassId"
                    class="zone-classes__empty"
                    data-testid="zone-classes-detail-empty"
                >
                    Select a class on the left to inspect its
                    member zones.
                </div>
                <template v-else>
                    <h3 class="zone-classes__detail-title">
                        Zones in {{ selectedClass?.name || "" }}
                    </h3>
                    <table
                        class="zone-classes__table"
                        data-testid="zone-classes-members-table"
                    >
                        <thead>
                            <tr>
                                <th>Slug</th>
                                <th>Display name</th>
                                <th>Zone max dims</th>
                                <th>Re-assign</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="zone in selectedZones"
                                :key="zone.id"
                                :data-testid="`zone-classes-zone-row-${zone.id}`"
                            >
                                <td>{{ zone.slug }}</td>
                                <td>{{ zone.display_name }}</td>
                                <td>
                                    {{ zone.default_max_width_in }}" x
                                    {{ zone.default_max_height_in }}"
                                </td>
                                <td>
                                    <select
                                        :value="zone.class_id ?? ''"
                                        :disabled="saving"
                                        :data-testid="`zone-classes-reassign-${zone.id}`"
                                        @change="
                                            onReassign(
                                                zone.id,
                                                $event.target.value
                                            )
                                        "
                                    >
                                        <option value="">
                                            (unclassified)
                                        </option>
                                        <option
                                            v-for="opt in classes"
                                            :key="opt.id"
                                            :value="opt.id"
                                        >
                                            {{ opt.name }}
                                        </option>
                                    </select>
                                </td>
                            </tr>
                            <tr
                                v-if="!selectedZones.length"
                                data-testid="zone-classes-no-members"
                            >
                                <td colspan="4">
                                    No member zones.
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </template>
            </section>
        </div>

        <section
            v-if="reclusterDiff.length"
            class="zone-classes__diff"
            data-testid="zone-classes-diff"
        >
            <h3 class="zone-classes__detail-title">
                Re-cluster proposals
            </h3>
            <p class="zone-classes__diff-help">
                Preview only -- nothing was saved. Use the per-zone
                dropdown above to apply individual changes, or
                Dismiss to ignore.
            </p>
            <table class="zone-classes__table">
                <thead>
                    <tr>
                        <th>Proposed name</th>
                        <th>Centroid (W x H)</th>
                        <th>Aspect band</th>
                        <th>Members</th>
                        <th>Status</th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="(p, idx) in reclusterDiff"
                        :key="`prop-${idx}-${p.class_name}`"
                        :data-testid="`zone-classes-proposal-${idx}`"
                    >
                        <td>{{ p.class_name }}</td>
                        <td>
                            {{ p.centroid_width }}" x
                            {{ p.centroid_height }}"
                        </td>
                        <td>
                            [{{ p.aspect_ratio_min }}, {{ p.aspect_ratio_max }}]
                        </td>
                        <td>
                            {{ p.member_zone_ids.length }}
                            ({{ p.member_zone_ids.join(", ") }})
                        </td>
                        <td>
                            <span
                                v-if="p.is_new"
                                class="zone-classes__badge zone-classes__badge--new"
                                data-testid="zone-classes-proposal-new"
                            >
                                New class
                            </span>
                            <span
                                v-else
                                class="zone-classes__badge"
                            >
                                Existing
                            </span>
                            <span
                                v-if="p.is_uncategorized"
                                class="zone-classes__badge zone-classes__badge--uncat"
                            >
                                Uncategorized
                            </span>
                        </td>
                    </tr>
                </tbody>
            </table>
            <button
                type="button"
                class="tf-btn tf-btn--ghost"
                data-testid="zone-classes-diff-dismiss"
                @click="onDismissDiff"
            >
                Dismiss
            </button>
        </section>

        <p
            v-if="errorMsg"
            class="zone-classes__error"
            data-testid="zone-classes-error"
        >
            {{ errorMsg }}
        </p>
    </section>
</template>

<script>
import axios from "@/axios";

export default {
    name: "PlacementZoneClasses",
    data() {
        return {
            classes: [],
            selectedClassId: null,
            selectedZones: [],
            reclusterDiff: [],
            loading: false,
            saving: false,
            reclustering: false,
            errorMsg: "",
        };
    },
    computed: {
        selectedClass() {
            return this.classes.find(
                (c) => c.id === this.selectedClassId
            );
        },
    },
    mounted() {
        this.fetchClasses();
    },
    methods: {
        async fetchClasses() {
            this.loading = true;
            this.errorMsg = "";
            try {
                const resp = await axios.get(
                    "/api/admin/placement-zone-classes"
                );
                this.classes = resp.data.classes || [];
            } catch (err) {
                this.errorMsg =
                    err.response?.data?.error ||
                    "Failed to load classes.";
            } finally {
                this.loading = false;
            }
        },
        async fetchZones(classId) {
            this.errorMsg = "";
            try {
                const resp = await axios.get(
                    `/api/admin/placement-zone-classes/${classId}/zones`
                );
                this.selectedZones = resp.data.zones || [];
            } catch (err) {
                this.errorMsg =
                    err.response?.data?.error ||
                    "Failed to load zones.";
            }
        },
        async onSelectClass(classId) {
            this.selectedClassId = classId;
            await this.fetchZones(classId);
        },
        async onReassign(zoneId, rawValue) {
            // The select binds string values; convert "" -> null and
            // numeric strings -> int. The backend validator rejects
            // anything else.
            let classId = null;
            if (rawValue !== "" && rawValue !== null) {
                classId = parseInt(rawValue, 10);
                if (Number.isNaN(classId)) {
                    this.errorMsg = "Invalid class selection.";
                    return;
                }
            }
            this.saving = true;
            this.errorMsg = "";
            try {
                await axios.patch(
                    `/api/admin/placement-zones/${zoneId}/class`,
                    { class_id: classId }
                );
                // Refresh both panes; the member-count on the left
                // and the binding on the right both move.
                await this.fetchClasses();
                if (this.selectedClassId) {
                    await this.fetchZones(this.selectedClassId);
                }
            } catch (err) {
                this.errorMsg =
                    err.response?.data?.error ||
                    "Failed to reassign zone.";
            } finally {
                this.saving = false;
            }
        },
        async onRecluster() {
            this.reclustering = true;
            this.errorMsg = "";
            try {
                const resp = await axios.post(
                    "/api/admin/placement-zone-classes/recluster"
                );
                this.reclusterDiff = resp.data.proposals || [];
            } catch (err) {
                this.errorMsg =
                    err.response?.data?.error ||
                    "Re-cluster failed.";
            } finally {
                this.reclustering = false;
            }
        },
        onDismissDiff() {
            this.reclusterDiff = [];
        },
    },
};
</script>

<style scoped>
.zone-classes {
    padding: 1.5rem;
}
.zone-classes__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.75rem;
}
.zone-classes__title {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 600;
}
.zone-classes__lede {
    color: #6b7280;
    max-width: 720px;
    margin-bottom: 1rem;
}
.zone-classes__panes {
    display: grid;
    grid-template-columns: 320px 1fr;
    gap: 1.5rem;
}
.zone-classes__list {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    border-right: 1px solid #e5e7eb;
    padding-right: 1rem;
}
.zone-classes__row {
    display: grid;
    grid-template-columns: 1fr auto;
    grid-template-rows: auto auto;
    column-gap: 0.5rem;
    padding: 0.5rem 0.75rem;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    background: white;
    text-align: left;
    cursor: pointer;
}
.zone-classes__row:hover {
    background: #f9fafb;
}
.zone-classes__row--active {
    background: #eef2ff;
    border-color: #6366f1;
}
.zone-classes__row-name {
    font-weight: 600;
    color: #111827;
}
.zone-classes__row-dims {
    grid-column: 2;
    grid-row: 1;
    color: #6b7280;
    font-size: 0.75rem;
}
.zone-classes__row-count {
    grid-column: 1 / -1;
    grid-row: 2;
    color: var(--tf-fg-4);
    font-size: 0.75rem;
}
.zone-classes__detail-title {
    margin: 0 0 0.5rem 0;
    font-size: 1.125rem;
    font-weight: 600;
}
.zone-classes__table {
    width: 100%;
    border-collapse: collapse;
}
.zone-classes__table th,
.zone-classes__table td {
    padding: 0.5rem 0.75rem;
    border-bottom: 1px solid #e5e7eb;
    text-align: left;
    font-size: 0.875rem;
}
.zone-classes__empty {
    padding: 2rem;
    text-align: center;
    color: var(--tf-fg-4);
}
.zone-classes__diff {
    margin-top: 1.5rem;
    padding: 1rem;
    background: #f9fafb;
    border-radius: 8px;
}
.zone-classes__diff-help {
    color: #6b7280;
    font-size: 0.875rem;
    margin-bottom: 0.75rem;
}
.zone-classes__badge {
    display: inline-block;
    padding: 0.125rem 0.5rem;
    background: #e5e7eb;
    color: #374151;
    border-radius: 999px;
    font-size: 0.75rem;
    margin-right: 0.25rem;
}
.zone-classes__badge--new {
    background: #d1fae5;
    color: #065f46;
}
.zone-classes__badge--uncat {
    background: #fef3c7;
    color: #92400e;
}
.zone-classes__error {
    color: #dc2626;
    margin-top: 0.75rem;
}
</style>
